import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const passwordSchema = z.object({
  password: z
    .string()
    .min(6, "Le mot de passe doit contenir au moins 6 caractères")
    .nonempty("Mot de passe requis"),
});

type ResetPasswordFormData = z.infer<typeof passwordSchema>;

const ChangePassword = () => {
  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (data: ResetPasswordFormData) => {
    setServerError(null);
    const { error } = await supabase.auth.updateUser({
      password: data.password,
    });
    if (error) {
      setServerError(error.message);
    } else {
      toast.success("Mot de passe modifié", {
        description: "Votre mot de passe a été mis à jour avec succès.",
      });
      navigate("/account");
    }
  };

  return (
    <div className="gradient-bg flex min-h-full flex-col px-4 py-6">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            className="p-2"
            onClick={() => navigate(-1)}
            aria-label="Retour"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-gray-700">
            Changer le mot de passe
          </h1>
        </div>
        <Card className="backdrop-blur-xs rounded-2xl border border-gray-100 bg-white/90 pt-4 shadow-lg">
          <CardContent>
            <Form {...form}>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nouveau mot de passe</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className={
                            (errors.password ?
                              "border-red-500 focus-visible:ring-red-500 "
                            : "focus-visible:ring-primary ") +
                            "pr-12 transition-all duration-150"
                          }
                          autoComplete="new-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormItem className="flex flex-row items-center gap-2">
                  <Switch
                    id="show-password"
                    checked={showPassword}
                    onCheckedChange={setShowPassword}
                  />
                  <FormLabel
                    htmlFor="show-password"
                    className="mb-0 cursor-pointer select-none"
                  >
                    Afficher le mot de passe
                  </FormLabel>
                </FormItem>
                {serverError && (
                  <p className="mt-2 text-sm text-red-500">{serverError}</p>
                )}
                <Button
                  type="submit"
                  className="w-full py-2.5 text-base"
                  disabled={isSubmitting}
                >
                  {isSubmitting ?
                    "Modification..."
                  : "Modifier le mot de passe"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;
